import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { isEqualObject } from '../helpers/arrayData';
import { UseAppDispatch } from '../store';
import { loadCompanies } from '@/shared/store/actions/CompanyActionCreators';

type RefreshOption = { isRefresh: boolean } | null | undefined;

const useCompaniesGrid = (stateKey: string = 'companies') => {
  const appDispatch = UseAppDispatch();

  const pageSize: number = 50;

  const filters = useSelector((state: any) => state?.companies?.filters);

  const [sortOptions, setSortOptions] = useState({ id: 'date', desc: false });

  const isRefreshData = useSelector((state: any) => state[stateKey]?.isRefreshData);

  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);

  const prevFiltersRef = useRef({});

  const transformToHierarchy = (companies: any) => {
    const map: any = {};
    const roots: any = [];
    companies.forEach((company: any) => {
      map[company.id] = { ...company, children: [] };
    });

    companies.forEach((company: any) => {
      if (company.parent && map[company.parent]) {
        map[company.parent].children.push(map[company.id]);
      } else {
        roots.push(map[company.id]);
      }
    });

    return roots;
  };

  const getCompaniesDataGrid = async (options: RefreshOption = null) => {
    setIsLoading(true);
    try {
      let params = { limit: pageSize, ...getPageParams(), ...filters };

      if (options?.isRefresh) {
        params = Object.assign(params, { limit: data.length, offset: 0 });
      }

      const { count = 0, next = null, previous = null, results = [] }: any = await appDispatch(loadCompanies(params)) ?? {};
      setDataCount(results.length);

      if (results?.length !== 0) {
        const hierarchicalData = Object.keys(filters).length > 0 ? results : transformToHierarchy(results);
        const _data: any = previous ? [...data, ...hierarchicalData] : hierarchicalData;
        setData(_data);
      } else {
        setData([]);
      }
      setNextPage(next ?? null);
      setTotalCount(count ?? null);
    } finally {
      setIsLoading(false);
    }
  };

  const getPageParams = () => {
    let params = {};
    if (nextPage) {
      if ((prevFiltersRef?.current as any)?.length === 0 && filters.length !== 0) {
        params = Object.assign(params);
      } else {
        const offsetValue = parseURLParam(nextPage, 'offset');
        if (offsetValue) {
          params = Object.assign(params, { offset: offsetValue });
        }
      }
    }
    if (!isEqualObject(filters, prevFiltersRef.current)) {
      params = Object.assign(params, { offset: 0 });
      prevFiltersRef.current = filters;
    }
    return params;
  };

  const parseURLParam = (nextPage: string, param: string) => {
    const url = new URL(nextPage);
    const searchParams = new URLSearchParams(url.search);
    return searchParams.get(param);
  };

  useEffect(() => {
    if (!prevFiltersRef.current || !isEqualObject(filters, prevFiltersRef.current)) {
      getCompaniesDataGrid();
      setSortOptions((prev) => {
        return { ...prev, desc: filters.sort === 'desc' ? true : false };
      });
    }
  }, [filters]);

  useEffect(() => {
    if (isRefreshData) getCompaniesDataGrid({ isRefresh: true });
  }, [isRefreshData]);

  return {
    data: data || [],
    dataTotalCount: totalCount,
    isLoading,
    pageSize,
    getCompaniesDataGrid,
    sortOptions: [sortOptions],
    filters,
    dataCount
  };
};

export default useCompaniesGrid;